import React from 'react';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import MenuItem from '@mui/material/MenuItem';
import InputAdornment from '@mui/material/InputAdornment';
import { COUNTRY_CODES } from '../../utils/countryCodes';

const EditStudentDialog = ({
  open,
  onClose,
  student,
  errors = {},
  onFieldChange,
  onSave,
  onPhoneAreaCodeChange,
}) => {
  // Format phone number for display
  const formatPhoneNumber = (input) => {
    if (!input) return '';
    
    const digits = input.replace(/\D/g, '');
    const areaCode = digits.substring(0, 3);
    const middle = digits.substring(3, 6);
    const end = digits.substring(6, 10);
    const extension = digits.substring(10, 15);
    
    let formatted = '';
    if (digits.length > 6) {
      formatted = `${areaCode} ${middle}-${end}`;
    } else if (digits.length > 3) {
      formatted = `${areaCode} ${middle}`;
    } else {
      formatted = digits;
    }
    
    if (extension) formatted += ` x${extension}`;
    return formatted;
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Edit Student</DialogTitle>
      <DialogContent sx={{ width: '800px' }}>
        {errors.general && (
          <Typography color="error" paragraph>
            {errors.general}
          </Typography>
        )}
        
        <Stack spacing={2} mt={1}>
          {/* Student ID (disabled) */}
          <TextField
            label="Student ID"
            name="studentId"
            value={student.studentId || ''}
            disabled
            fullWidth
          />
          
          {/* Fields */}
          {Object.keys(student).map((key) => {
            // Skip rendering certain fields
            if (key === 'studentId' || 
                key === 'studentCreationDate' || 
                key === 'phoneAreaCode' ||
                key === '_links') {
              return null;
            }

            // handling for phone field
            if (key === 'phoneNumber') {
              return (
                <div key={key}>
                  {errors[key] && (
                    <Typography variant="body2" color="error" gutterBottom>
                      {errors[key]}
                    </Typography>
                  )}
                  <TextField
                    fullWidth
                    label="Phone Number"
                    name={key}
                    value={formatPhoneNumber(student[key])}
                    onChange={onFieldChange}
                    error={!!errors[key]}
                    inputProps={{
                      maxLength: 20,
                      inputMode: 'tel'
                    }}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                            <TextField
                                select
                                variant="standard"
                                value={student.phoneAreaCode || '+1'}
                                onChange={onPhoneAreaCodeChange}
                            >
                            {COUNTRY_CODES.map((option) => (
                                <MenuItem key={option.code} value={option.code}>
                                    {option.flag} {option.code}
                                </MenuItem>
                                ))}
                            </TextField>
                        </InputAdornment>
                      ),
                    }}
                  />
                </div>
              );
            }

            // activationStatus
            if (key === 'activationStatus') {
              return (
                <TextField
                  key={key}
                  label="User Created"
                  name={key}
                  value={student.activationStatus ? 'Yes' : 'No'}
                  disabled
                  fullWidth
                />
              );
            }

            // Regular fields
            return (
              <div key={key}>
                {errors[key] && (
                  <Typography variant="body2" color="error" gutterBottom>
                    {errors[key]}
                  </Typography>
                )}
                <TextField
                  fullWidth
                  label={key.replace(/([A-Z])/g, ' $1')
                      .replace(/^./, str => str.toUpperCase())}
                  name={key}
                  value={student[key] || ''}
                  onChange={onFieldChange}
                  error={!!errors[key]}
                  multiline={key === "notes"}
                  rows={key === "notes" ? 4 : 1}
                  inputProps={{
                    maxLength: 255
                  }}
                />
              </div>
            );
          })}

          {/* Creation Date (disabled) */}
          {student.studentCreationDate && (
            <TextField
              label="Creation Date"
              value={student.studentCreationDate}
              disabled
              fullWidth
            />
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button 
          onClick={onSave}
          variant="contained"
          color="primary"
        >
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default EditStudentDialog;